import React, { Component } from 'react';
import { graphql, compose } from 'react-apollo';
import gql from 'graphql-tag';
import Spinner from '../../Spinner';
import PdfPreview from '../PdfPreview';
import PropTypes from 'prop-types';
import Message from '../../Message';
import moment from 'moment';
import { TIMEOUT_TRESHOLD } from '../../../constants';
import T from 'i18n-react';
import _ from 'lodash';

class PdfPreviewContainer extends Component {
  state = {
    error: '',
    loading: false,
  };

  handleDocumentError = error => {
    const { history, match, data } = this.props;
    const token = match.params.id;
    const errorMessage = error.networkError.result.message;
    this.setState({ loading: false });
    if (errorMessage === 'LoaLawyers::Service::LoaLawyerExists') {
      this.setState({ error: 'LoaLawyerExists' });
      this.redirectTimeout = setTimeout(() => {
        history.push(`/loa-lawyer/${token}/sign`);
      }, TIMEOUT_TRESHOLD);
      return;
    }
    if (errorMessage === 'LoaLawyers::Service::LawFirmChanged') {
      this.setState({ error: 'LawFirmChanged' });
      data.refetch().then(() => this.delayedClearError());
      return;
    }
    this.setState({
      error: 'serverError',
    });
  };

  generatePdf = data => {
    const { history, match, onPdfGenerate, generatedDocument } = this.props;
    const token = match.params.id;
    this.setState({ loading: true });
    onPdfGenerate(data)
      .then(({ data }) => generatedDocument.refetch())
      .then(() => {
        this.setState({ loading: false });
        history.push(`/loa-lawyer/${token}/sign`);
      })
      .catch(error => this.handleDocumentError(error));
  };

  componentWillUnmount = () => {
    clearTimeout(this.redirectTimeout);
    clearTimeout(this.clearErrorTimeout);
  };

  delayedClearError = () => {
    this.clearErrorTimeout = setTimeout(() => {
      this.setState({ error: '' });
    }, TIMEOUT_TRESHOLD);
  };

  get error() {
    const {error} = this.state
    const {data} = this.props
    if (error) {
      return error;
    }
    const lawFirm = _.get(data, 'loaLawyerDocumentData.lawFirm');
    if (lawFirm === null) {
      return 'serverError';
    }
  }

  componentDidUpdate = ({ data: prevData = {} }, prevState) => {
    const { data = {} } = this.props;
    const prevLoaLawyerDocData = prevData.loaLawyerDocumentData;
    const prevError = _.get(prevLoaLawyerDocData, 'error');
    const currentError = _.get(data, 'loaLawyerDocumentData.error');
    const prevLawFirm = _.get(prevLoaLawyerDocData, 'lawFirm', {});
    const lawFirm = _.get(data, 'loaLawyerDocumentData.lawFirm', {});
    if (prevLoaLawyerDocData && ((prevLawFirm && prevLawFirm.id) !== (lawFirm && lawFirm.id))) {
      this.setState({ error: 'LawFirmChanged' });
      this.delayedClearError();
    }
    if (!prevError && currentError) {
      this.setState({ error: 'serverError' });
    }
  };

  render() {
    const { data, allCountries, paxData, guardianData, paxDisplayName } = this.props;
    const { loading } = this.state;
    const { loading: queryLoading } = data;
    const lawFirm = _.get(data, 'loaLawyerDocumentData.lawFirm');
    const error = this.error;
    const displaySpinner = error === 'LoaLawyerExists';

    if (loading || queryLoading) {
      return <Spinner />;
    }

    if (error) {
      return Message(T.translate(error), null, displaySpinner);
    }

    return (
      <PdfPreview
        validateGeneratePdfForm
        onSubmit={this.generatePdf}
        paxData={paxData}
        guardianData={guardianData}
        allCountries={allCountries}
        paxDisplayName={paxDisplayName}
        key={lawFirm && lawFirm.id}
        {...this.props}
      />
    );
  }
}

const LoaLawyerDocumentData = gql`
  query LoaLawyerDocumentData($token: String!) {
    loaLawyerDocumentData(token: $token) {
      passengerTicket {
        passengerFullName
      }
      lawFirm {
        id
        name
        city
        country
        street
      }
      airline {
        name
      }
    }
  }
`;

const GeneratePdf = gql`
  mutation CreateLoaLawyerMutation($input: CreateLoaLawyerMutationInput!) {
    createLoaLawyer(input: $input) {
      loaLawyer {
        id
      }
    }
  }
`;

const generatedDocument = gql`
  query loaLawyer($token: String!) {
    loaLawyer(token: $token) {
      id
      documentUrl
    }
  }
`;

export default compose(
  graphql(LoaLawyerDocumentData, {
    options: props => {
      const token = props.match.params.id;
      return {
        variables: { token },
        pollInterval: 10000,
      };
    },
  }),
  graphql(generatedDocument, {
    name: 'generatedDocument',
    options: props => {
      const token = props.match.params.id;
      return {
        variables: { token },
      };
    },
  }),
  graphql(GeneratePdf, {
    props: ({
      mutate,
      ownProps: { data = {}, paxAdult, match, paxData, guardianData },
    }) => ({
      onPdfGenerate: inputData => {
        if (paxAdult === undefined) {
          paxAdult =
            moment().diff(moment(paxData.birthDate), 'years', true) >= 18;
        }
        const guardian = _.mapValues(guardianData, value => value || undefined)
        const token = match.params.id;
        const lawFirmId = _.get(data, 'loaLawyerDocumentData.lawFirm.id')

        const {
          termsAndConditions1,
          termsAndConditions2,
          termsAndConditions3,
          signedIn,
        } = inputData;
        return mutate({
          variables: {
            input: {
              token,
              passengerTicket: {
                passengerFirstName: paxData.firstName,
                passengerLastName: paxData.lastName,
                dateOfBirth: paxData.birthDate,
                placeOfBirth: paxData.birthPlace,
                profession: paxData.profession,
                street: paxData.street,
                postCode: paxData.postalCode,
                city: paxData.city,
                country: paxData.country,
              },
              loaLawyer: {
                lawFirmId,
                signedIn,
                adult: paxAdult,
                consent: termsAndConditions1,
                tcConsent: termsAndConditions2,
                personalDataConsent: termsAndConditions3,
                guardianFirstName: guardian.firstName,
                guardianLastName: guardian.lastName,
                guardianBirthDate: guardian.birthDate,
                guardianBirthPlace: guardian.birthPlace,
                guardianStreet: guardian.street,
                guardianPostCode: guardian.postalCode,
                guardianCity: guardian.city,
                guardianCountry: guardian.country,
                guardianProfession: guardian.profession,
              },
            },
          },
        });
      },
    }),
  }),
)(PdfPreviewContainer);

PdfPreviewContainer.propTypes = {
  guardianData: PropTypes.object.isRequired,
  paxData: PropTypes.object,
  paxAdult: PropTypes.bool,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  loaLawyer: PropTypes.object.isRequired,
  paxDisplayName: PropTypes.string,
};



// WEBPACK FOOTER //
// ./src/components/LoaLawyer/containers/PdfPreviewContainer.js